import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import { deleteStudent, exportStudents, listStudentCounselors, listStudents } from '../../api/students';
import { downloadBlob } from '../../utils/downloadBlob';
import { listLookup } from '../../api/masterData';
import { StudentListItem } from '../../types/student';
import { useBranchFilter } from '../../context/branchContext';
import { useAuth } from '../../context/authContext';
import { TableToolbar } from '../../components/table/TableToolbar';
import { PageHeader } from '../../components/PageHeader';
import { StudentApplicationWizard } from './wizard/StudentApplicationWizard';
import { DeleteStudentConfirmDialog } from './DeleteStudentConfirmDialog';
import { NumberLabel } from '../../components/NumberLabel';

const extractErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong.') : 'Something went wrong.';

export const StudentsListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { branches, selectedBranchId } = useBranchFilter();
  const { user, hasPermission } = useAuth();
  // Super Admin only (Phase 66) — deliberately not `hasPermission('students',
  // 'delete')`, since Director also holds that permission via the 'ALL'
  // shortcut (see permissions.ts) but must not see this destructive action.
  const isSuperAdmin = user?.role.name === 'Super Admin';
  // A Counselor only ever sees their own students (enforced by the API), so
  // the Counselor filter is offered to everyone else.
  const isCounselor = user?.role.name === 'Counselor';
  const canExport = hasPermission('students', 'export');

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
  const [search, setSearch] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [resumeStudentId, setResumeStudentId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StudentListItem | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Admission Officer automatic-visibility (§2/§11): University/Programme
  // filter directly on the student's own preferredUniversityId/
  // preferredProgramId (already-existing columns) — Programme narrows to
  // the selected University's own courses, same dependent-dropdown pattern
  // Step1ApplicationDetails.tsx already uses at registration time.
  const [universityId, setUniversityId] = useState<number | ''>('');
  const [programId, setProgramId] = useState<number | ''>('');
  // Counselor filter: students registered by this counselor (Registered By).
  const [counselorId, setCounselorId] = useState<number | ''>('');
  // Branch filter — only for roles that can see more than one branch (the
  // same roles as the top-bar branch selector; everyone else is pinned to
  // their own branch by the API). Starts from, and follows, the top-bar
  // choice.
  const [branchFilter, setBranchFilter] = useState<number | ''>(selectedBranchId ?? '');
  useEffect(() => {
    setBranchFilter(selectedBranchId ?? '');
    setCounselorId('');
  }, [selectedBranchId]);
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  // Registered students KPI card's click-through destination
  // (`/students?registrationPaymentApproved=true`) — a lazy initializer
  // (not useMemo) since, unlike documentsPending's one-shot banner, this
  // is an ordinary dropdown the Admission Officer should be free to
  // change afterward without the URL fighting them.
  const [registrationPaymentFilter, setRegistrationPaymentFilter] = useState<'' | 'approved'>(() =>
    new URLSearchParams(location.search).get('registrationPaymentApproved') === 'true' ? 'approved' : '',
  );
  // Admission Officer Dashboard correction: the Pending Documents card's
  // click-through destination (`/students?documents=pending`) — derived
  // from the URL on every render (not frozen at mount) so the "Clear
  // filter" action below, which navigates to the bare pathname, actually
  // takes effect.
  const documentsPending = useMemo(
    () => new URLSearchParams(location.search).get('documents') === 'pending',
    [location.search],
  );

  useEffect(() => {
    // "+ New" menu bug fix: depends on location.state, not `[]` — see the
    // identical fix (and its full explanation) in LeadsListPage.tsx.
    // Navigating here from "+ New" while already on /students would
    // otherwise silently do nothing, since this component never remounts
    // for a same-route navigation.
    if ((location.state as { quickCreate?: boolean } | null)?.quickCreate) {
      setResumeStudentId(null);
      setWizardOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const universitiesQuery = useQuery({
    queryKey: ['lookup', '/master-data/universities'],
    queryFn: () => listLookup('/master-data/universities', { activeOnly: true }),
  });
  const coursesQuery = useQuery({
    queryKey: ['lookup', '/master-data/courses', universityId],
    queryFn: () => listLookup('/master-data/courses', { activeOnly: true, universityId }),
    enabled: universityId !== '',
  });

  const studentsQuery = useQuery({
    queryKey: [
      'students',
      paginationModel.page,
      paginationModel.pageSize,
      search,
      branchFilter,
      documentsPending,
      universityId,
      programId,
      registrationPaymentFilter,
      counselorId,
    ],
    queryFn: () =>
      listStudents({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: search || undefined,
        branchId: branchFilter || undefined,
        documentsPending: documentsPending || undefined,
        preferredUniversityId: universityId || undefined,
        preferredProgramId: programId || undefined,
        registrationPaymentApproved: registrationPaymentFilter === 'approved' ? true : undefined,
        counselorId: counselorId || undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const counselorsQuery = useQuery({
    queryKey: ['students', 'counselors', branchFilter],
    queryFn: () => listStudentCounselors(branchFilter || undefined),
    enabled: !isCounselor,
  });

  // Exports exactly what the list shows: same filters, every page.
  const handleExport = async (format: 'xlsx' | 'pdf') => {
    setExporting(format);
    setExportError(null);
    try {
      const { blob, filename } = await exportStudents({
        format,
        search: search || undefined,
        branchId: branchFilter || undefined,
        documentsPending: documentsPending || undefined,
        preferredUniversityId: universityId || undefined,
        preferredProgramId: programId || undefined,
        registrationPaymentApproved: registrationPaymentFilter === 'approved' ? true : undefined,
        counselorId: counselorId || undefined,
      });
      downloadBlob(blob, filename);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Couldn't export the students list");
    } finally {
      setExporting(null);
    }
  };

  const invalidateStudents = () => queryClient.invalidateQueries({ queryKey: ['students'] });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteStudent(id),
    onSuccess: () => {
      setDeleteTarget(null);
      setDeleteError(null);
      invalidateStudents();
    },
    onError: (err) => setDeleteError(extractErrorMessage(err)),
  });

  const columns: GridColDef<StudentListItem>[] = useMemo(
    () => [
      { field: 'applicationNumber', headerName: 'Application #', width: 200, renderCell: ({ row }) => <NumberLabel value={row.applicationNumber} kind="application" /> },
      { field: 'studentNumber', headerName: 'Student #', width: 200, renderCell: ({ row }) => <NumberLabel value={row.studentNumber} kind="student" /> },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        valueGetter: (_v, row) => `${row.firstName} ${row.lastName}`,
      },
      { field: 'phone', headerName: 'Phone', width: 140 },
      { field: 'email', headerName: 'Email', flex: 1 },
      {
        field: 'status',
        headerName: 'Status',
        width: 110,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.row.isDraft ? 'Draft' : 'Registered'}
            color={params.row.isDraft ? 'default' : 'success'}
          />
        ),
      },
      {
        field: 'nationality',
        headerName: 'Nationality',
        width: 140,
        // The student's own nationality — not `country`, the preferred
        // study destination this column used to show.
        valueGetter: (_v, row) => row.nationalityCountry?.name ?? '—',
      },
      {
        field: 'university',
        headerName: 'University',
        width: 200,
        valueGetter: (_v, row) => row.preferredUniversity?.name ?? '—',
      },
      {
        field: 'universityCountry',
        headerName: 'Country',
        width: 130,
        valueGetter: (_v, row) => row.preferredUniversity?.country?.name ?? '—',
      },
      {
        field: 'programme',
        headerName: 'Programme',
        width: 160,
        valueGetter: (_v, row) => row.preferredProgram?.name ?? '—',
      },
      {
        field: 'lead',
        headerName: 'From lead',
        width: 110,
        valueGetter: (_v, row) => row.lead?.leadNumber ?? '—',
      },
      ...(isSuperAdmin
        ? [
            {
              field: 'actions',
              headerName: 'Actions',
              width: 90,
              sortable: false,
              filterable: false,
              renderCell: (params: { row: StudentListItem }) => (
                <Tooltip title="Delete student">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteError(null);
                      setDeleteTarget(params.row);
                    }}
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </Tooltip>
              ),
            } satisfies GridColDef<StudentListItem>,
          ]
        : []),
    ],
    [isSuperAdmin],
  );

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={SchoolIcon}
        color="#12B76A"
        title="Students"
        subtitle="Registered students and their profiles."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setResumeStudentId(null);
              setWizardOpen(true);
            }}
          >
            New student
          </Button>
        }
      />

      {documentsPending && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate(location.pathname, { replace: true })}>
              Clear filter
            </Button>
          }
        >
          Showing students with at least one required document not yet accepted.
        </Alert>
      )}

      <TextField
        placeholder="Search by name, phone, email, student #…"
        size="small"
        sx={{ mb: 2, width: 360 }}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="University"
          sx={{ width: 220 }}
          value={universityId}
          onChange={(e) => {
            setUniversityId(e.target.value ? Number(e.target.value) : '');
            setProgramId('');
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
        >
          <MenuItem value="">
            <em>All universities</em>
          </MenuItem>
          {universitiesQuery.data?.map((u) => (
            <MenuItem key={u.id as number} value={u.id as number}>
              {String(u.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Programme"
          sx={{ width: 200 }}
          disabled={universityId === ''}
          value={programId}
          onChange={(e) => {
            setProgramId(e.target.value ? Number(e.target.value) : '');
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
        >
          <MenuItem value="">
            <em>All programmes</em>
          </MenuItem>
          {coursesQuery.data?.map((c) => (
            <MenuItem key={c.id as number} value={c.id as number}>
              {String(c.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Registration payment"
          sx={{ width: 200 }}
          value={registrationPaymentFilter}
          onChange={(e) => {
            setRegistrationPaymentFilter(e.target.value as '' | 'approved');
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
        </TextField>
        {branches.length > 0 && (
          <TextField
            select
            size="small"
            label="Branch"
            sx={{ width: 200 }}
            value={branchFilter}
            onChange={(e) => {
              setBranchFilter(e.target.value ? Number(e.target.value) : '');
              // The counselor list narrows to the branch, so start over.
              setCounselorId('');
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
          >
            <MenuItem value="">
              <em>All Branches</em>
            </MenuItem>
            {branches.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        {!isCounselor && (
          <TextField
            select
            size="small"
            label="Counselor"
            sx={{ width: 220 }}
            value={counselorId}
            onChange={(e) => {
              setCounselorId(e.target.value ? Number(e.target.value) : '');
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
          >
            <MenuItem value="">
              <em>All Counselors</em>
            </MenuItem>
            {counselorsQuery.data?.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.firstName} {c.lastName}
              </MenuItem>
            ))}
          </TextField>
        )}
        {canExport && (
          <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' } }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              disabled={exporting !== null}
              onClick={() => handleExport('xlsx')}
            >
              {exporting === 'xlsx' ? 'Exporting…' : 'Excel'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              disabled={exporting !== null}
              onClick={() => handleExport('pdf')}
            >
              {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
            </Button>
          </Stack>
        )}
      </Stack>

      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      )}

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={studentsQuery.data?.data ?? []}
          columns={columns}
          rowCount={studentsQuery.data?.meta.total ?? 0}
          loading={studentsQuery.isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          slots={{ toolbar: TableToolbar }}
          disableRowSelectionOnClick
          onRowClick={(params) => {
            if (params.row.isDraft) {
              setResumeStudentId(params.row.id);
              setWizardOpen(true);
            } else {
              navigate(`/students/${params.row.id}`);
            }
          }}
        />
      </Box>

      <StudentApplicationWizard
        open={wizardOpen}
        resumeStudentId={resumeStudentId}
        onClose={() => setWizardOpen(false)}
        onSaved={invalidateStudents}
      />

      <DeleteStudentConfirmDialog
        open={!!deleteTarget}
        studentName={deleteTarget ? `${deleteTarget.firstName} ${deleteTarget.lastName}` : ''}
        studentNumber={deleteTarget?.studentNumber ?? ''}
        errorMessage={deleteError}
        submitting={deleteMutation.isPending}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteError(null);
        }}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
      />
    </Box>
  );
};
